// Workout taxonomy — single source of truth for category enums, labels, and helpers.

export const CATEGORIES = [
    { value: "speed", label: "Speed", desc: "Force-velocity at high-velocity end" },
    { value: "strength", label: "Strength", desc: "Maximum force production" },
    { value: "stamina", label: "Stamina", desc: "Sport-specific endurance" },
    { value: "suppleness", label: "Suppleness", desc: "Mobility, range of motion" },
    { value: "skill", label: "Skill", desc: "Application — sprint, jump, change of direction" },
    { value: "extras", label: "Extras", desc: "Recovery, prehab, accessory" },
    { value: "daily", label: "Daily Workouts", desc: "Fresh daily training sessions" },
];

export const CATEGORY_LABELS = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

export const EXERCISE_TYPES = [
    { value: "compound", label: "Compound" },
    { value: "accessory", label: "Accessory" },
    { value: "plyo", label: "Plyometric" },
    { value: "cardio", label: "Cardio" },
    { value: "speed", label: "Speed" },
    { value: "power", label: "Power" },
    { value: "mobility", label: "Mobility" },
    { value: "core", label: "Core" },
];

export const EXERCISE_TYPE_LABELS = Object.fromEntries(EXERCISE_TYPES.map((t) => [t.value, t.label]));

export const RM_TYPES = [
    { value: "", label: "—" },
    { value: "1RM", label: "1RM" },
    { value: "AMRAP", label: "AMRAP" },
];

/**
 * Pulls the RM target out of an exercise name. Returns a string suitable
 * for the `reps` field. Returns null when there's no RM hint.
 *
 *   "5RM Goblet Box Squat"            → "5"
 *   "1RM Safety Squat Bar Box Squat"  → "1"
 *   "10RM Front Squat"                → "10"
 *   "AMRAP Pull-ups"                  → "AMRAP"
 *   "Back Squat"                      → null
 */
export function rmFromExerciseName(name) {
    if (!name || typeof name !== "string") return null;
    // Look for a number directly attached to "RM" (case-insensitive),
    // anchored at a word boundary so we don't grab digits from random
    // strings like "1/4 Depth".
    const m = name.match(/(?:^|\W)(\d+)\s*RM\b/i);
    if (m) return m[1];
    if (/\bAMRAP\b/i.test(name)) return "AMRAP";
    return null;
}

export const ADVANCEMENT_TYPES = [
    { value: "", label: "None" },
    { value: "bw_multiplier", label: "Bodyweight × multiplier" },
    { value: "absolute", label: "Absolute weight (lb)" },
    { value: "reps", label: "Reps achieved" },
];

/**
 * Returns the user-facing label, placeholder, and unit hint for the
 * "goal" input on a level, based on the chosen advancement type. Returns
 * null when no goal input should be shown (advancement type = None).
 */
export function advancementGoalConfig(advancementType) {
    if (advancementType === "bw_multiplier") {
        return {
            label: "Bodyweight multiplier goal",
            placeholder: "e.g. 1.5",
            unit: "× BW",
            help: "When the athlete can lift this multiple of their bodyweight, they're ready to move to the next level.",
        };
    }
    if (advancementType === "absolute") {
        return {
            label: "Weight goal",
            placeholder: "e.g. 135",
            unit: "lb",
            help: "When the athlete can lift this much, they're ready to move to the next level.",
        };
    }
    if (advancementType === "reps") {
        return {
            label: "Reps goal",
            placeholder: "e.g. 10",
            unit: "reps",
            help: "When the athlete hits this rep count on the final set, they're ready to move to the next level.",
        };
    }
    return null;
}

export const LINK_TYPES = [
    { value: "", label: "Standalone" },
    { value: "superset", label: "Superset" },
    { value: "circuit", label: "Circuit" },
];

export function emptySet(setNum) {
    return { set_num: setNum, percent_max: "", reps: "", rest_mins: "" };
}

/**
 * Returns auto-fill defaults for a fresh set in a STRENGTH workout based on
 * the exercise NAME (which is where coaches encode the rep target — e.g.
 * "5RM Goblet Box Squat"). Coaches can still override anything; the intent
 * is "save typing on the 90% case", not lock them in.
 *
 *   Name "NRM ..."  → 100% / N / 2min
 *   Name "AMRAP ..."→ 100% / AMRAP / 2min
 *   Anything else   → returns null (no autofill)
 */
export function strengthSetDefaults(exerciseName) {
    const reps = rmFromExerciseName(exerciseName);
    if (!reps) return null;
    return { percent_max: "100", reps, rest_mins: "2" };
}

export function emptyLevel(levelNum) {
    return {
        level: levelNum,
        name: "",
        exercise_id: "",
        exercise_name: "",
        rm_type: "",
        bw_multiplier: null,
        advancement_type: null,
        advancement_value: null,
        based_off_exercise_id: null,
        alt_exercise_id: null,
        sets_data: [emptySet(1)],
    };
}

/**
 * One exercise slot inside a block. In a Standalone block there is one
 * slot. In a Superset/Circuit block there are 2+, executed in rounds.
 * Each slot owns its own list of difficulty levels.
 */
export function emptyExercise(slot) {
    return {
        slot,
        levels: [emptyLevel(1)],
    };
}

export function emptyBlock(letter, num) {
    return {
        id: null,
        name: `${letter}${num}`,
        block_group: letter,
        isCircuit: false,
        link_type: null,
        coach_note: "",
        exercises: [emptyExercise(1)],
    };
}

/**
 * Coerce a block loaded from the API into the new multi-exercise shape.
 * Legacy blocks (pre-2026-02-17) only had `block.levels`; we lift those
 * into `block.exercises[0].levels` so the rest of the builder only ever
 * has to read `block.exercises`. Idempotent.
 */
export function normalizeBlock(block) {
    if (!block) return block;
    if (Array.isArray(block.exercises) && block.exercises.length) return block;
    if (Array.isArray(block.levels) && block.levels.length) {
        return { ...block, exercises: [{ slot: 1, levels: block.levels }] };
    }
    return { ...block, exercises: [emptyExercise(1)] };
}

/**
 * Returns the set of round numbers used by ANY exercise's first level
 * inside the block. Used by the per-set inclusion UI so the coach can
 * see all rounds at a glance, even rounds that some exercises skip.
 */
export function blockRoundNumbers(block) {
    if (!block?.exercises) return [];
    const set = new Set();
    for (const ex of block.exercises) {
        const lv = ex.levels?.[0];
        if (!lv) continue;
        for (const s of lv.sets_data || []) {
            const n = Number(s.set_num);
            if (Number.isFinite(n) && n > 0) set.add(n);
        }
    }
    return [...set].sort((a, b) => a - b);
}

// Letter sequence for new blocks: A, B, C, ...
export function nextBlockLetter(blocks) {
    const used = new Set(blocks.map((b) => (b.block_group || b.name?.[0] || "A").toUpperCase()));
    for (let c = 65; c <= 90; c++) {
        if (!used.has(String.fromCharCode(c))) return String.fromCharCode(c);
    }
    return "X";
}
